import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { RefreshCw, Pencil, Check, X, Radio } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Tile({ label, value, sub, testid }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-base font-semibold" data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function WalletBalances({ wallet, usd, onSaved }) {
  const [chain, setChain] = useState(null);
  const [checking, setChecking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ usdc_balance: "", usdt_balance: "" });
  const id = wallet.id;
  const funded = wallet.usdc_balance > 0 || wallet.usdt_balance > 0;

  const check = async () => {
    setChecking(true);
    try {
      const r = await api.get(`/vault-wallets/${id}/onchain`);
      setChain(r.data);
      if (r.data.status !== "live") toast.error(r.data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const startEdit = () => { setForm({ usdc_balance: wallet.usdc_balance, usdt_balance: wallet.usdt_balance }); setEditing(true); };
  const save = async () => {
    try {
      await api.put(`/vault-wallets/${id}/balances`, { usdc_balance: Number(form.usdc_balance || 0), usdt_balance: Number(form.usdt_balance || 0) });
      toast.success("Balances updated");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const live = chain?.status === "live";
  return (
    <div className="mt-4 space-y-3 border-t border-border pt-4" data-testid={`wallet-balances-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Balances</span>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${funded ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`} data-testid={`wallet-status-${id}`}>{funded ? "Funded" : "Empty"}</span>
        {chain && (
          <span className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${live ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`} data-testid={`wallet-chain-status-${id}`}>
            <Radio className="h-3 w-3" /> {live ? `On-chain · ${chain.network}` : "On-chain unavailable"}
          </span>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        <Tile label="USD" value={`$${money(usd)}`} sub="Shared card account balance" testid={`wallet-usd-${id}`} />
        {editing ? (
          <>
            <label className="space-y-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">USDC
              <Input type="number" min="0" step="0.01" value={form.usdc_balance} onChange={(e) => setForm({ ...form, usdc_balance: e.target.value })} placeholder="0.00" data-testid={`wallet-usdc-input-${id}`} />
            </label>
            <label className="space-y-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">USDT
              <Input type="number" min="0" step="0.01" value={form.usdt_balance} onChange={(e) => setForm({ ...form, usdt_balance: e.target.value })} placeholder="0.00" data-testid={`wallet-usdt-input-${id}`} />
            </label>
          </>
        ) : (
          <>
            <Tile label="USDC" value={money(wallet.usdc_balance)} sub={live ? `On-chain: ${money(chain.usdc)}` : "Vault tracked"} testid={`wallet-usdc-${id}`} />
            <Tile label="USDT" value={money(wallet.usdt_balance)} sub={live ? `On-chain: ${money(chain.usdt)}` : "Vault tracked"} testid={`wallet-usdt-${id}`} />
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {editing ? (
          <>
            <Button size="sm" onClick={save} data-testid={`wallet-balances-save-${id}`}><Check className="mr-1 h-3.5 w-3.5" /> Save balances</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid={`wallet-balances-cancel-${id}`}><X className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={startEdit} data-testid={`wallet-balances-edit-${id}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Adjust USDC/USDT</Button>
        )}
        <Button size="sm" variant="outline" onClick={check} disabled={checking} data-testid={`wallet-onchain-check-${id}`}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${checking ? "animate-spin" : ""}`} /> {checking ? "Checking…" : "Check on-chain"}
        </Button>
      </div>
    </div>
  );
}
